import { observable, computed, makeObservable, action } from 'mobx'
import Wkt from 'wicket/wicket'
import axios from 'axios'
import api from 'api.js'
import _ from 'lodash'
import { selectionTypes } from 'constants.js'
import { geoJSONBounds, smoothFactor } from 'utils'
import { catchError } from './ErrorStore'

// An item in the tree of browsable admin boundaries
export class BoundaryItem {
  @observable id
  @observable name
  @observable ids
  @observable expanded
  @observable zip_code_loaded
  @observable is_zip_code
  @observable fetching_data
  @observable loaded
  @observable boundaries
  @observable.ref child_geometries
  @observable parent_names
  @observable.ref geoJSON
  @observable.ref bounds
  @observable hasChildren
  @observable additional_info = []
  @observable contact_detail = { given_name: '', surname: '', email: '', address: '' }

  constructor ({
    id,
    name,
    ids,
    expanded,
    boundaries,
    bounds,
    parent_names,
    zip_code_admin_level,
    child_geometries,
    geoJSON,
    hasChildren,
    haschildren
  } = {}) {
    makeObservable(this)
    this.id = id
    this.name = name || ''
    this.ids = ids || []
    this.loaded = false
    this.bounds = bounds || []
    this.geoJSON = geoJSON
    this.fetching_data = false
    this.is_zip_code = false
    this.expanded = expanded || false
    this.zip_code_loaded = false
    this.boundaries = boundaries || []
    this.parent_names = parent_names
    this.child_geometries = child_geometries || []
    this.zip_code_admin_level = zip_code_admin_level
    this.hasChildren = hasChildren !== undefined ? hasChildren : haschildren
  }

  @catchError async loadSubBoundaries (useZipCodes) {
    let list
    let is_zip_code = false
    this.fetching_data = true
    if (this.ids.length > 0 && !this.zip_code_admin_level) {
      const res = await api.getZipCodeAdminLevel(this.ids[0])
      this.zip_code_admin_level = res.data.results.zip_code_admin_level
    }
    // zip_code_admin_level is the one that should be replaced with zip codes
    // if the zip_code_admin_level is equal to the length of indices, fetch the zip codes
    if (useZipCodes && this.ids.length === this.zip_code_admin_level) {
      const parent_id = this.is_zip_code ? this.id : null
      list = await api.getZipCodeList(this.ids, parent_id)
      this.zip_code_loaded = true
      is_zip_code = true
      this.loaded = false
    } else {
      list = await api.getBoundaries(this.ids, useZipCodes)
      console.log('boundary list', list)
      this.loaded = true
      this.zip_code_loaded = false
      is_zip_code = false
    }
    this.boundaries.replace(_(list.data.results).map(item => {
      let bounds = []
      if ('bbox' in item) {
        bounds = JSON.parse(item.bbox)
      }
      const boundary = new BoundaryItem({
        id: item.id,
        name: item.name,
        zip_code_admin_level: this.zip_code_admin_level,
        ids: this.ids.slice(),
        expanded: false,
        bounds,
        boundaries: null,
        hasChildren: item.hasChildren !== undefined ? item.hasChildren : item.haschildren
      })
      boundary.is_zip_code = is_zip_code
      // This sub-boundary's list of ids (ID_0, ID_1, etc)
      // should be its parent's list appended with the value
      // of this sub-boundary's most-specific ID
      //
      // If parent is United States with ID_0 of 840, the parent's
      // `ids` array will be [840]. When sub-boundaries are loaded
      // for United States and Colorado has ID_1 of 6,
      // Colorado's `ids` array will be [840, 6]
      //
      const property = `ID_${this.ids.length}`
      if (property in item) {
        boundary.ids.push(item[property])
      }

      return boundary
    }).sortBy('name').value())
    this.fetching_data = false
  }

  @catchError async loadGeometry () {
    const wkt = new Wkt.Wkt()
    let response
    let polygon
    if (this.is_zip_code) {
      response = await api.postZipCodeBoundaryGeoJSON({ id: this.id })
      polygon = response.data[0].polygon
    } else {
      const response = await api.postAdminBoundaryGeoJSON({ id: [this.id] })
      polygon = wkt.read(response.data.results[0].geometry).toJson()
    }
    this.geoJSON = polygon
  }

  bounds () { return geoJSONBounds(this.geoJSON) }

  @catchError async add (targetGroupId) {
    // console.log(this.geoJSON)
    // const wkt = new window.Wkt.Wkt()
    // wkt.read(JSON.stringify(this.geoJSON))
    // if zip code, save zip code boundary
    if (this.is_zip_code) {
      const result = await api.postZipCodeBoundary({
        remote_db_id: this.id,
        target_group: targetGroupId
      })
      return result.data
    }
    const result = await api.postBoundary({
      remote_db_id: this.id,
      target_group: targetGroupId
    })
    return result.data
  }

  @computed get smoothFactor () {
    return smoothFactor(this.geoJSON)
  }
}

// Top-level admin boundary list model
class BoundaryList {
  @observable root = new BoundaryItem()
  @observable selectedBoundary = null
  @observable _search = ''
  @observable error
  @observable searchResults = []
  @observable selectedBoundaries = []
  @observable savingBoundaries = false
  @observable searchLoading = false
  @observable useZipCodes = false
  @observable additional_info = []
  debouncedBoundarySearch
  searchCancel = axios.CancelToken.source()

  @computed get search () { return this._search }
  set search (search) {
    this.error = null
    this.selectedBoundary = null
    this._search = search
    const trimmedSearch = (search || '').trim()
    if (trimmedSearch === '') {
      this.searchLoading = false
      this.searchResults = []
    } else {
      this.searchLoading = true
    }
    this.debouncedBoundarySearch()
  }

  // As the user types, update search results. Debounce
  // so we don't make a request for each key press, and
  // cancel any pending requests so we don't end up using
  // old results
  @catchError async boundarySearch () {
    const trimmedSearch = (this.search || '').trim()
    if (trimmedSearch !== '') {
      this.searchCancel.cancel()
      this.searchCancel = axios.CancelToken.source()

      try {
        const response = await api.postBoundarySearch({
          name: trimmedSearch,
          start: 0,
          n: 20
        }, { cancelToken: this.searchCancel.token }, this.useZipCodes)

        this.searchLoading = false
        // Sort results alphabetically by name
        const sortedResults = _.sortBy(response.data.results, 'name')
        this.searchResults = _.map(sortedResults, result => {
          const boundary = new BoundaryItem(result)
          boundary.ids = result.id_structure
          boundary.is_zip_code = result.is_zip_code
          boundary.bounds = JSON.parse(result.bbox)
          return boundary
        })
      } catch (e) {
        if (!axios.isCancel(e)) {
          throw e
        }
      }
    } else {
      this.searchLoading = false
      this.searchResults = []
    }
  }

  // adding bulk boundaries to target group
  async add () {
    const zipCodes = this.selectedBoundaries.filter(boundary => boundary.is_zip_code).map(boundary => ({
      remote_db_id: boundary.remote_db_id,
      target_group: boundary.targetGroupId
    }))
    const adminBoundaries = this.selectedBoundaries.filter(boundary => !boundary.is_zip_code).map(boundary => ({
      remote_db_id: boundary.remote_db_id,
      target_group: boundary.targetGroupId
    }))
    const response = []

    if (adminBoundaries.length > 0) {
      try {
        const result = await api.postBoundary({ boundaries: adminBoundaries, target_group: adminBoundaries[0].target_group })
        response.push(result.data)
        // Clear selected boundaries after successful add to prevent duplicates on next add
        this.selectedBoundaries = this.selectedBoundaries.filter(boundary => boundary.is_zip_code)
      } catch (error) {
        const err = new Error('Admin boundary error')
        err.type = 'admin_boundary_error'
        err.data = error.response.data
        throw err
      }
    }
    if (zipCodes.length > 0) {
      try {
        const result = await api.postZipCodeBoundary({ boundaries: zipCodes, target_group: zipCodes[0].target_group })
        response.push(result.data)
        // Clear selected boundaries after successful add
        this.selectedBoundaries = this.selectedBoundaries.filter(boundary => !boundary.is_zip_code)
      } catch (error) {
        console.log('error while adding zip codes')
        const err = new Error('Zip code error')
        err.type = 'zip_code_error'
        err.data = error.response.data
        throw err
      }
    }

    return response
  }

  constructor () {
    makeObservable(this)
    this.debouncedBoundarySearch = _.debounce(() => {
      this.boundarySearch()
    }, 250)

    this.root.loadSubBoundaries()
  }

  get selection () {
    return {
      item: this,
      type: selectionTypes.BOUNDARY_LIST
    }
  }

  @action getTypeLabel () {
    return 'N/A'
  }

  // TODO: there is a discrepency here with how "isZipCode" is named. the naming needs to be consistent for ease of use. Will fix later.
  addBoundaryToSelection ({ id, name, targetGroupId, ids, isZipCode }) {
    this.selectedBoundaries.push({ remote_db_id: id, name, targetGroupId, ids, is_zip_code: isZipCode })
  }

  addBoundariesToSelection (boundaries, targetGroupId) {
    boundaries.forEach(({ id, name, ids, is_zip_code }) => {
      this.selectedBoundaries.push({ remote_db_id: id, name, targetGroupId, ids, is_zip_code })
    })
  }

  removeBoundaryFromSelection ({ id }) {
    const filtered = this.selectedBoundaries.filter(x => String(x.remote_db_id || x.id) !== String(id))
    this.selectedBoundaries = filtered
  }

  removeBoundariesFromSelection (boundaries) {
    const idsToRemove = boundaries.map(b => String(b.id || b.remote_db_id))
    const filtered = this.selectedBoundaries.filter(x => !idsToRemove.includes(String(x.remote_db_id || x.id)))
    this.selectedBoundaries = filtered
  }

  toggleUseZipCodes () {
    this.useZipCodes = !this.useZipCodes
  }
}

export default BoundaryList
